import { string, boolean, date } from 'yup';
import { UserGeneratedAnswersEnum, UserGeneratedAnswersKeys } from './types';
import { generateSchema, SchemaLogicType } from '@papa/components';
import dropins from '../emails/dropins.json';

const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const earliestSupportedYear = currentYear - 81;
const latestSupportedYear = currentYear - 13;
const supportedMonth = currentDate.getMonth();
const supportedDate = currentDate.getDate();

const isValidString = (value: string) => {
    return value && value.length > 0;
};
const transformOptionalBoolean = () => {
    return boolean()
        .transform(value => !!value)
        .optional();
};

const personalInformationSchema = {
    first_name: string().required('First name is required.'),
    middle_name: string().optional(),
    last_name: string().required('Last name is required.'),
    preferred_name: string().optional(),
    prev_lname: string().optional(),
    gender: string().required('Gender is required.'),
    gender_iden: string().optional(),
    birth_date: date()
        .typeError('A valid date of birth is required.')
        .required('A valid date of birth is required.')
        .min(
            new Date(earliestSupportedYear, supportedMonth, supportedDate),
            `The minimum supported year is ${earliestSupportedYear}. Please contact help support for assistance.`
        )
        .max(new Date(latestSupportedYear, supportedMonth, supportedDate), `You must be ${currentYear - latestSupportedYear} years or older to apply.`),
    ssn: string()
        .matches(/^$|\d\d\d-\d\d-\d\d\d\d/, 'Please provide a valid social security number.')
        .max(11)
        .optional(),
};

const contactInfoSchema = {
    phone_home: string()
        .matches(/^$|\(\d\d\d\)-\d\d\d-\d\d\d\d/, 'Please provide a valid US phone number for home phone.')
        .optional(),
    phone_mobile: string()
        .matches(/^$|\(\d\d\d\)-\d\d\d-\d\d\d\d/, 'Please provide a valid US phone number for cell phone.')
        .when('phone_home', {
            is: (value: string) => !!value,
            then: schema =>
                schema.when('optin_sms_ind', {
                    is: true,
                    then: schema => schema.required('A cell phone number is required to opt in to text message updates.'),
                    otherwise: schema => schema.optional(),
                }),
            otherwise: schema => schema.required('Please enter either a home phone or cell phone number.'),
        }),
    optin_sms_ind: boolean().optional(),
    address_line_1: string().required('Address is required.'),
    address_line_2: string().optional(),
    city: string().required('City is required.'),
    state: string().when('country', {
        is: (value: string) => value && ['USA', 'CAN'].includes(value.toUpperCase()),
        then: schema => schema.required('State is required.'),
        otherwise: schema => schema.optional(),
    }),
    zip: string().when('country', {
        is: (value: string) => value && ['USA'].includes(value.toUpperCase()),
        then: schema => schema.required('Zip/Postal Code is required').min(5, 'Zip/Postal Code must be at least 5 characters.'),
        otherwise: schema => schema.optional(),
    }),
    country: string().required('Country is required.'),
    mailing_addr_same_as_perm_addr: string().required('Please indicate if your mailing address is the same as your permanent address.'),
    mailing_address_line_1: string().optional(),
    mailing_address_line_2: string().optional(),
    mailing_city: string().optional(),
    mailing_state: string().optional(),
    mailing_zip: string().optional(),
    mailing_country: string().optional(),
    mailing_addr_effective_date: date()
        .transform(value => (value === '' || 'Invalid Date' ? null : value))
        .nullable(),
};

const citizenshipSchema = {
    citizenship: string().optional(),
    citizenship_type: string().optional(),
    birth_country: string().optional(),
    birth_state: string().optional(),
    citizen_country: string().optional(),
    alien_registration_number: string().optional(),
    us_study_radio: string().when('citizenship_type', {
        is: (value: string) => value && value === '3',
        then: schema => schema.required('Please indicate if you intend to study in the US.'),
        otherwise: schema => schema.optional(),
    }),
    us_residency_radio: string().optional(),
    citizenship_visa_status: string().optional(),
    intended_visa_type: string().optional(),
    current_visa_number: string().optional(),
};

const ethnicitySchema = {
    ethnicity: string().optional(),
    race_american_indian: boolean().optional(),
    race_asian: boolean().optional(),
    race_black: boolean().optional(),
    race_native_hawaiian: boolean().optional(),
    race_white: boolean().optional(),
    tribe_radio: string().optional(),
    tribe_affiliation: string().optional(),
    tribe_affiliation_other: string().optional(),
};

const militarySchema = {
    military_status: string().optional(),
    military_branch: string().optional(),
    military_status_specified: string().optional(),
    military_tuition_assistance: string().optional(),
    military_self_edu_benefits: string().optional(),
    military_self_veterans_benefits: string().optional(),
    military_dependent: string().optional(),
    military_spouse_status: string().optional(),
    military_spouse_benefits: string().optional(),
    law_enforcement: string().optional(),
};

const legacySchema = {
    legacy_ind: string().optional(),
    legacy_relationship: string().optional(),
    legacy_first_name: string().optional(),
    legacy_last_name: string().optional(),
    legacy_2_relationship: string().optional(),
    legacy_2_first_name: string().optional(),
    legacy_2_last_name: string().optional(),
    legacy_3_relationship: string().optional(),
    legacy_3_first_name: string().optional(),
    legacy_3_last_name: string().optional(),
};

const yourPlansSchema = {
    program_type: string().required('Online Program Type is required.'),
    applied_major: string().when('program_type', {
        is: (value: string) => !!value === true,
        then: schema => schema.required('Please select a major.'),
        otherwise: schema => schema.optional(),
    }),
    app_concentration: string().optional(),
    ctl_int_coll_univ: transformOptionalBoolean(),
    ctl_int_elm_school: transformOptionalBoolean(),
    ctl_int_ind_priv: transformOptionalBoolean(),
    ctl_int_sec_school: transformOptionalBoolean(),
    ctl_int_sec_comm_coll: transformOptionalBoolean(),
    ce_int_geotechnical: transformOptionalBoolean(),
    ce_int_structural: transformOptionalBoolean(),
    ce_int_transportation: transformOptionalBoolean(),
    ce_int_water_resources: transformOptionalBoolean(),
    ga_int_european_studies: transformOptionalBoolean(),
    ga_int_global_studies: transformOptionalBoolean(),
    ga_int_la_studies: transformOptionalBoolean(),
    ga_int_asian_studies: transformOptionalBoolean(),
    ga_int_me_studies: transformOptionalBoolean(),
    hw_int_water_man: transformOptionalBoolean(),
    hw_int_water_qual: transformOptionalBoolean(),
    hw_int_water_quan: transformOptionalBoolean(),
    li_int_data: transformOptionalBoolean(),
    li_int_info_tech: transformOptionalBoolean(),
    li_int_acad: transformOptionalBoolean(),
    li_int_pub: transformOptionalBoolean(),
    li_int_school: transformOptionalBoolean(),
    li_int_special: transformOptionalBoolean(),
    li_int_arc_pres: transformOptionalBoolean(),
    li_int_youth_serv: transformOptionalBoolean(),
    se_int_appl_ba: transformOptionalBoolean(),
    se_int_fund_se: transformOptionalBoolean(),
    se_int_sec_trans: transformOptionalBoolean(),
    cs_int: string().optional(),
    geog_env_int: string().optional(),
    ngem_int_engineering: transformOptionalBoolean(),
    ngem_int_management: transformOptionalBoolean(),
    entry_term: string().when('applied_major', {
        is: (value: string) => isValidString(value),
        then: schema => schema.required('Enroll term is required.'),
        otherwise: schema => schema.optional(),
    }),
};

const educationalHxSchema = {
    writing_sample: string().optional(),
    college_transcript: string().optional(),
    identification: string().required('Please upload a form of identification.').oneOf(['ANSWERED'], 'Please upload a form of identification.'),
    parent_education: string().optional(),
    parent_2_education: string().optional(),
    prev_attend: string().required(`Please indicate if you have previously attended ${dropins.partner_name}.`),
    prev_attend_from_month: string().optional(),
    prev_attend_to_month: string().optional(),
    prev_attend_from_year: string().optional(),
    prev_attend_to_year: string().optional(),
    sooner_id: string().optional(),
    college_degree: string().optional(),
    college_gpa: string().optional(),
    college_credits: string().optional(),
    college_attend_from_month: string().when('college_name', {
        is: (value: string) => isValidString(value),
        then: schema => schema.required('Month is required for start date.'),
        otherwise: schema => schema.optional(),
    }),
    college_attend_to_month: string().when('college_name', {
        is: (value: string) => isValidString(value),
        then: schema => schema.required('Month is required for end date.'),
        otherwise: schema => schema.optional(),
    }),
    college_attend_from_year: string().when('college_name', {
        is: (value: string) => isValidString(value),
        then: schema => schema.required('Year is required for start date.'),
        otherwise: schema => schema.optional(),
    }),
    college_attend_to_year: string().when('college_name', {
        is: (value: string) => isValidString(value),
        then: schema => schema.required('Year is required for end date.'),
        otherwise: schema => schema.optional(),
    }),
    college_2_degree: string().optional(),
    college_2_gpa: string().optional(),
    college_2_credits: string().optional(),
    college_2_attend_from_month: string().optional(),
    college_2_attend_to_month: string().optional(),
    college_2_attend_from_year: string().optional(),
    college_2_attend_to_year: string().optional(),
    college_3_degree: string().optional(),
    college_3_gpa: string().optional(),
    college_3_credits: string().optional(),
    college_3_attend_from_month: string().optional(),
    college_3_attend_to_month: string().optional(),
    college_3_attend_from_year: string().optional(),
    college_3_attend_to_year: string().optional(),
    college_4_degree: string().optional(),
    college_4_gpa: string().optional(),
    college_4_credits: string().optional(),
    college_4_attend_from_month: string().optional(),
    college_4_attend_to_month: string().optional(),
    college_4_attend_from_year: string().optional(),
    college_4_attend_to_year: string().optional(),
    college_5_degree: string().optional(),
    college_5_gpa: string().optional(),
    college_5_credits: string().optional(),
    college_5_attend_from_month: string().optional(),
    college_5_attend_to_month: string().optional(),
    college_5_attend_from_year: string().optional(),
    college_5_attend_to_year: string().optional(),
    college_name: string().required('College name is required.'),
    college_2_name: string().optional(),
    college_3_name: string().optional(),
    college_4_name: string().optional(),
    college_5_name: string().optional(),
    college_address_line_1: string().optional(),
    college_2_address_line_1: string().optional(),
    college_3_address_line_1: string().optional(),
    college_4_address_line_1: string().optional(),
    college_5_address_line_1: string().optional(),
    college_city: string().optional(),
    college_2_city: string().optional(),
    college_3_city: string().optional(),
    college_4_city: string().optional(),
    college_5_city: string().optional(),
    college_state: string().optional(),
    college_2_state: string().optional(),
    college_3_state: string().optional(),
    college_4_state: string().optional(),
    college_5_state: string().optional(),
    college_zip: string().optional(),
    college_2_zip: string().optional(),
    college_3_zip: string().optional(),
    college_4_zip: string().optional(),
    college_5_zip: string().optional(),
    college_ceeb: string().optional(),
    college_2_ceeb: string().optional(),
    college_3_ceeb: string().optional(),
    college_4_ceeb: string().optional(),
    college_5_ceeb: string().optional(),
    counselor_first_name: string().optional(),
    counselor_last_name: string().optional(),
    counselor_email_address: string()
        .matches(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/, 'Please provide a valid counselor email address.')
        .optional(),
    hs_ceeb: string().optional(),
    hs_address_line_1: string().optional(),
    hs_city: string().optional(),
    hs_state: string().optional(),
    hs_name: string().required('Please provide the name of your most recent high school.'),
    hs_2_ceeb: string().optional(),
    hs_2_address_line_1: string().optional(),
    hs_2_city: string().optional(),
    hs_2_state: string().optional(),
    hs_2_name: string().optional(),
    hs_3_ceeb: string().optional(),
    hs_3_address_line_1: string().optional(),
    hs_3_city: string().optional(),
    hs_3_state: string().optional(),
    hs_3_name: string().optional(),
    hs_4_ceeb: string().optional(),
    hs_4_address_line_1: string().optional(),
    hs_4_city: string().optional(),
    hs_4_state: string().optional(),
    hs_4_name: string().optional(),
    hs_5_ceeb: string().optional(),
    hs_5_address_line_1: string().optional(),
    hs_5_city: string().optional(),
    hs_5_state: string().optional(),
    hs_5_name: string().optional(),
    hs_attend_from_month: string().optional(),
    hs_attend_to_month: string().optional(),
    hs_attend_from_year: string().optional(),
    hs_attend_to_year: string().optional(),
    hs_2_attend_from_month: string().optional(),
    hs_2_attend_to_month: string().optional(),
    hs_2_attend_from_year: string().optional(),
    hs_2_attend_to_year: string().optional(),
    hs_3_attend_from_month: string().optional(),
    hs_3_attend_to_month: string().optional(),
    hs_3_attend_from_year: string().optional(),
    hs_3_attend_to_year: string().optional(),
    hs_4_attend_from_month: string().optional(),
    hs_4_attend_to_month: string().optional(),
    hs_4_attend_from_year: string().optional(),
    hs_4_attend_to_year: string().optional(),
    hs_5_attend_from_month: string().optional(),
    hs_5_attend_to_month: string().optional(),
    hs_5_attend_from_year: string().optional(),
    hs_5_attend_to_year: string().optional(),
    hs_gpa: string()
        .matches(/^$|\d.\d\d/, 'Please provide a valid GPA and round to two decimal places.')
        .max(4, 'Please round to two decimal places.'),
    gpa_scale: string().when('hs_gpa', {
        is: (value: string) => !!value,
        then: schema => schema.required('GPA scale is required.'),
        otherwise: schema => schema.optional(),
    }),
    grad_month: string().when('hs_name', {
        is: (value: string) => isValidString(value),
        then: schema => schema.required('Graduation month is required.'),
        otherwise: schema => schema.optional(),
    }),
    grad_year: string().when('hs_name', {
        is: (value: string) => isValidString(value),
        then: schema => schema.required('Graduation year is required.'),
        otherwise: schema => schema.optional(),
    }),
    financial_aid: string().required('Please indicate if you intend to apply for financial aid.'),
    attendance_plans: string().required('Please indicate your attendance plans.'),
    housing_plans: string().required('Please indicate your housing plans.'),
    prev_credit: string().optional(),
    prev_credit_semester: string().optional(),
    prev_applied: string().optional(),
    prev_applied_semester: string().optional(),
    personal_statement_essay: string().optional(),
    personal_statement_doc: string().when('personal_statement_essay', {
        is: (value: string) => isValidString(value),
        then: schema => schema.optional(),
        otherwise: schema => schema.required('Please provide a personal statement.'),
    }),
    resume_essay: string().optional(),
    resume_doc: string().optional(),
    ged: string().optional(),
    homeschool: string().optional(),
    special_program: string().optional(),
};

const activitySchema = {
    activity_name: string().optional(),
    activity_freshman: string().optional(),
    activity_sophomore: string().optional(),
    activity_junior: string().optional(),
    activity_senior: string().optional(),
    activity_2_name: string().optional(),
    activity_2_freshman: string().optional(),
    activity_2_sophomore: string().optional(),
    activity_2_junior: string().optional(),
    activity_2_senior: string().optional(),
    activity_3_name: string().optional(),
    activity_3_freshman: string().optional(),
    activity_3_sophomore: string().optional(),
    activity_3_junior: string().optional(),
    activity_3_senior: string().optional(),
    activity_4_name: string().optional(),
    activity_4_freshman: string().optional(),
    activity_4_sophomore: string().optional(),
    activity_4_junior: string().optional(),
    activity_4_senior: string().optional(),
};

const testScoresSchema = {
    gmat_taken: string().when('applied_major', {
        is: (value: string) => (value && value === '34') || (value && value === '56'),
        then: schema => schema.required('Please indicate if you have taken the GMAT.'),
        otherwise: schema => schema.optional(),
    }),
    gmat_taken_month: string().optional(),
    gmat_taken_year: string().optional(),
    gmat_score_composite: string().when('gmat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d\d\d/, 'Please provide a valid GMAT Composite Score. Scores range from 200-800.'),
        otherwise: schema => schema.optional(),
    }),
    gmat_score_composite_percentile: string().when('gmat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GMAT Composite Percentile.'),
        otherwise: schema => schema.optional(),
    }),
    gmat_score_quant: string().when('gmat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GMAT Quantitative Score. Scores range from 0-60.'),
        otherwise: schema => schema.optional(),
    }),
    gmat_score_quant_percentile: string().when('gmat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GMAT Quantitative Percentile.'),
        otherwise: schema => schema.optional(),
    }),
    gmat_score_verbal: string().when('gmat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GMAT Verbal Score. Scores range from 0-60.'),
        otherwise: schema => schema.optional(),
    }),
    gmat_score_verbal_percentile: string().when('gmat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GMAT Verbal Percentile.'),
        otherwise: schema => schema.optional(),
    }),
    gmat_score_writing: string().when('gmat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GMAT Analytical Writing Score. Scores range from 0-6 in half-point increments.'),
        otherwise: schema => schema.optional(),
    }),
    gmat_score_writing_percentile: string().when('gmat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GMAT Analytical Writing Percentile.'),
        otherwise: schema => schema.optional(),
    }),
    gmat_score_reasoning: string().when('gmat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GMAT Integrated Reasoning Score. Scores range from 1-8.'),
        otherwise: schema => schema.optional(),
    }),
    gmat_score_reasoning_percentile: string().when('gmat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GMAT Integrated Reasoning Percentile.'),
        otherwise: schema => schema.optional(),
    }),
    gmat_plan_month: string().optional(),
    gmat_plan_year: string().optional(),
    gre_taken: string().when('applied_major', {
        is: (value: string) => (value && value === '34') || (value && value === '56'),
        then: schema => schema.required('Please indicate if you have taken the GRE.'),
        otherwise: schema => schema.optional(),
    }),
    gre_taken_month: string().optional(),
    gre_taken_year: string().optional(),
    gre_score_verbal: string().when('gre_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d\d\d/, 'Please provide a valid GRE Verbal Reasoning Score. Scores range from 130-170.'),
        otherwise: schema => schema.optional(),
    }),
    gre_score_verbal_percentile: string().when('gre_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GRE Verbal Reasoning Percentile.'),
        otherwise: schema => schema.optional(),
    }),
    gre_score_quant: string().when('gre_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d\d\d/, 'Please provide a valid GRE Quantitative Reasoning Score. Scores range from 130-170.'),
        otherwise: schema => schema.optional(),
    }),
    gre_score_quant_percentile: string().when('gre_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GRE Quantitative Reasoning Percentile.'),
        otherwise: schema => schema.optional(),
    }),
    gre_score_writing: string().when('gre_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GRE Analytical Writing Score. Scores range from 0-6 in half-point increments.'),
        otherwise: schema => schema.optional(),
    }),
    gre_score_writing_percentile: string().when('gre_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid GRE Analytical Writing Percentile.'),
        otherwise: schema => schema.optional(),
    }),
    gre_plan_month: string().optional(),
    gre_plan_year: string().optional(),
    toefl_taken: string().optional(),
    toefl_taken_month: string().optional(),
    toefl_taken_year: string().optional(),
    toefl_score: string().when('toefl_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema =>
            schema.when('citizenship_type', {
                is: (value: string) => (value && value === '2') || (value && value === '3'),
                then: schema => schema.matches(/\d/, 'Please provide a valid TOEFL Score.'),
                otherwise: schema => schema.optional(),
            }),
    }),
    toefl_score_listening: string().when('toefl_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema =>
            schema.when('citizenship_type', {
                is: (value: string) => (value && value === '2') || (value && value === '3'),
                then: schema => schema.matches(/\d/, 'Please provide a valid TOEFL Listening Score.'),
                otherwise: schema => schema.optional(),
            }),
    }),
    toefl_score_speaking: string().when('toefl_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema =>
            schema.when('citizenship_type', {
                is: (value: string) => (value && value === '2') || (value && value === '3'),
                then: schema => schema.matches(/\d/, 'Please provide a valid TOEFL Speaking Score.'),
                otherwise: schema => schema.optional(),
            }),
    }),
    toefl_score_reading: string().when('toefl_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema =>
            schema.when('citizenship_type', {
                is: (value: string) => (value && value === '2') || (value && value === '3'),
                then: schema => schema.matches(/\d/, 'Please provide a valid TOEFL Reading Score.'),
                otherwise: schema => schema.optional(),
            }),
    }),
    toefl_score_writing: string().when('toefl_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema =>
            schema.when('citizenship_type', {
                is: (value: string) => (value && value === '2') || (value && value === '3'),
                then: schema => schema.matches(/\d/, 'Please provide a valid TOEFL Writing Score.'),
                otherwise: schema => schema.optional(),
            }),
    }),
    toefl_plan_month: string().optional(),
    toefl_plan_year: string().optional(),
    ielts_taken: string().optional(),
    ielts_taken_month: string().optional(),
    ielts_taken_year: string().optional(),
    ielts_score_overall: string().when('ielts_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema =>
            schema.when('citizenship_type', {
                is: (value: string) => (value && value === '2') || (value && value === '3'),
                then: schema => schema.matches(/\d/, 'Please provide a valid IELTS Score.'),
                otherwise: schema => schema.optional(),
            }),
    }),
    ielts_score_listening: string().when('ielts_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema =>
            schema.when('citizenship_type', {
                is: (value: string) => (value && value === '2') || (value && value === '3'),
                then: schema => schema.matches(/\d/, 'Please provide a valid IELTS Listening Score.'),
                otherwise: schema => schema.optional(),
            }),
    }),
    ielts_score_reading: string().when('ielts_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema =>
            schema.when('citizenship_type', {
                is: (value: string) => (value && value === '2') || (value && value === '3'),
                then: schema => schema.matches(/\d/, 'Please provide a valid IELTS Reading Score.'),
                otherwise: schema => schema.optional(),
            }),
    }),
    ielts_score_writing: string().when('ielts_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema =>
            schema.when('citizenship_type', {
                is: (value: string) => (value && value === '2') || (value && value === '3'),
                then: schema => schema.matches(/\d/, 'Please provide a valid IELTS Writing Score.'),
                otherwise: schema => schema.optional(),
            }),
    }),
    ielts_score_speaking: string().when('ielts_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema =>
            schema.when('citizenship_type', {
                is: (value: string) => (value && value === '2') || (value && value === '3'),
                then: schema => schema.matches(/\d/, 'Please provide a valid IELTS Speaking Score.'),
                otherwise: schema => schema.optional(),
            }),
    }),
    ielts_plan_month: string().optional(),
    ielts_plan_year: string().optional(),
    sat_taken: string().optional(),
    sat_plan_month: string().optional(),
    sat_plan_year: string().optional(),
    sat_score_math: string().when('sat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d\d\d/, 'Please provide a valid SAT Math Score. Scores range from 200-800.'),
        otherwise: schema => schema.optional(),
    }),
    sat_score_reading_writing: string().when('sat_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d\d\d/, 'Please provide a valid SAT Evidence-Based Reading and Writing Score. Scores range from 200-800.'),
        otherwise: schema => schema.optional(),
    }),
    sat_taken_again: string().optional(),
    sat_taken_unsure: string().optional(),
    act_taken: string().optional(),
    act_plan_month: string().optional(),
    act_plan_year: string().optional(),
    act_score_math: string().when('act_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid ACT Math Score. Scores range from 1-36.'),
        otherwise: schema => schema.optional(),
    }),
    act_score_english: string().when('act_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid ACT English Score. Scores range from 1-36.'),
        otherwise: schema => schema.optional(),
    }),
    act_score_composite: string().when('act_taken', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.matches(/\d/, 'Please provide a valid ACT Composite Score. Scores range from 1-36.'),
        otherwise: schema => schema.optional(),
    }),
    act_taken_again: string().optional(),
    act_taken_unsure: string().optional(),
    toefl_taken_again: string().optional(),
    toefl_taken_unsure: string().optional(),
    ielts_taken_again: string().optional(),
    ielts_taken_unsure: string().optional(),
    test_taken_ind: string().optional(),
    sat_intended_month: string().optional(),
    sat_intended_year: string().optional(),
    act_intended_month: string().optional(),
    act_intended_year: string().optional(),
    toefl_intended_month: string().optional(),
    toefl_intended_year: string().optional(),
    ielts_intended_month: string().optional(),
    ielts_intended_year: string().optional(),
};

const finalStepsSchema = {
    conviction: string().required('Please indicate if you have ever been convicted of a felony.'),
    conviction_month: string().optional(),
    conviction_year: string().optional(),
    conviction_comment: string().when('conviction', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.required('Please explain the circumstances of the felony conviction.'),
        otherwise: schema => schema.optional(),
    }),
    suspended: string().required('Please indicate if you have ever been suspended or expelled.'),
    suspended_month: string().optional(),
    suspended_year: string().optional(),
    suspended_comment: string().when('suspended', {
        is: (value: string) => value && value.toUpperCase() === 'Y',
        then: schema => schema.required('Please explain the circumstances of your suspension or expulsion.'),
        otherwise: schema => schema.optional(),
    }),
    protective: string().optional(),
    protective_month: string().optional(),
    protective_year: string().optional(),
    protective_comment: string().optional(),
    prohibited: string().optional(),
    prohibited_month: string().optional(),
    prohibited_year: string().optional(),
    prohibited_comment: string().optional(),
    discharge: string().optional(),
    discharge_month: string().optional(),
    discharge_year: string().optional(),
    discharge_comment: string().optional(),
    legal_agree: boolean().required('You must accept these terms to submit your application.').oneOf([true], 'You must accept these terms to submit your application.'),
};

export const schema = generateSchema<SchemaLogicType<UserGeneratedAnswersKeys>, typeof UserGeneratedAnswersEnum>(
    {
        ...contactInfoSchema,
        ...personalInformationSchema,
        ...citizenshipSchema,
        ...ethnicitySchema,
        ...militarySchema,
        ...legacySchema,
        ...yourPlansSchema,
        ...educationalHxSchema,
        ...activitySchema,
        ...testScoresSchema,
        ...finalStepsSchema,
    },
    UserGeneratedAnswersEnum
);
